/**
 * Brand injection + provenance — the never-blocks seam between the Brand
 * Brain and generation.
 *
 * Two reliability rules, enforced here so every caller inherits them:
 *
 *   1. Generation NEVER blocks on brand knowledge. `buildBrandSystemFragment`
 *      wraps composeBrandPrompt in a catch — any thrown error (malformed
 *      brain, bad budget, future compose bug) degrades to the shipped
 *      neutral fallback fragment instead of failing the draft. Reliability
 *      over cleverness: an ungoverned-but-labelled output beats no output.
 *
 *   2. Outputs are NEVER silently ungoverned. `withBrandProvenance` stamps
 *      the provenance envelope with exactly which brand knowledge shaped
 *      the output ({brandHash, injectedEntryIds, truncated, fallbackUsed})
 *      plus a human verify badge — either
 *      'Brand Brain applied (N directives, hash abc123)' or the honest
 *      'Brand Brain: neutral fallback'.
 *
 * PURE: no store, no firebase, no network, no clock.
 */

import type { BrandBrain, BrandScope } from './schema';
import {
  brandFallbackFragment,
  composeBrandPrompt,
  type ComposedBrandPrompt,
} from './compose';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Compose result + the honest "did we fall back?" flag. */
export interface BrandFragmentResult extends ComposedBrandPrompt {
  fallbackUsed: boolean;
}

/** The brand fields stamped onto a provenance envelope. */
export interface BrandProvenanceMeta {
  brandHash: string;
  injectedEntryIds: string[];
  truncated: boolean;
  fallbackUsed: boolean;
}

// ---------------------------------------------------------------------------
// buildBrandSystemFragment
// ---------------------------------------------------------------------------

/**
 * Compose the brand system-prompt fragment for one output scope. NEVER
 * throws: any compose error — and the honest empty-brain case — collapses
 * to the shipped neutral fallback with `fallbackUsed: true`, so generation
 * always proceeds and provenance always says what happened.
 */
export function buildBrandSystemFragment(
  brain: BrandBrain,
  scope: BrandScope,
  tokenBudget: number,
): BrandFragmentResult {
  try {
    const composed = composeBrandPrompt(brain, scope, tokenBudget);
    return { ...composed, fallbackUsed: composed.hash === 'neutral' };
  } catch {
    return { ...brandFallbackFragment(), fallbackUsed: true };
  }
}

// ---------------------------------------------------------------------------
// Badge
// ---------------------------------------------------------------------------

/** Short display form of a fragment hash ('djb2:1a2b3c4d' → '1a2b3c4d'). */
function shortHash(hash: string): string {
  return hash.replace(/^djb2:/, '');
}

/**
 * Human verify badge for a composed fragment — the visible proof that an
 * output was (or honestly was not) brand-governed.
 */
export function brandBadge(result: BrandFragmentResult): string {
  if (result.fallbackUsed || result.hash === 'neutral') {
    return 'Brand Brain: neutral fallback';
  }
  const n = result.injected.length;
  return `Brand Brain applied (${n} directive${n === 1 ? '' : 's'}, hash ${shortHash(result.hash)})`;
}

// ---------------------------------------------------------------------------
// withBrandProvenance
// ---------------------------------------------------------------------------

/** Project a compose result into the envelope meta fields. */
export function brandProvenanceMeta(result: BrandFragmentResult): BrandProvenanceMeta {
  return {
    brandHash: result.hash,
    injectedEntryIds: result.injected.map((e) => e.id),
    truncated: result.truncated,
    fallbackUsed: result.fallbackUsed,
  };
}

/**
 * Extend a provenance envelope with the brand fields + verify badge WITHOUT
 * mutating the original (same pattern as agent/provenance.attachProvenance).
 */
export function withBrandProvenance<T extends object>(
  baseEnvelope: T,
  composeResult: BrandFragmentResult,
): T & BrandProvenanceMeta & { badge: string } {
  return {
    ...baseEnvelope,
    ...brandProvenanceMeta(composeResult),
    badge: brandBadge(composeResult),
  };
}
